import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SearchableSelect } from '../../../../Common/Class/Select/SearchableSelec2';
import { Modal } from '../../../../Common/fModal/Modal';
import { showToast } from '../../../../Common/fToast/ToastCallback';
import { FunctionalInput } from '../../../../Common/fInputs';
import { PageLoader } from '../../../../Common/Components/PageLoader';
import useTdsList from './useTdsList';

const BulkTdsLineItem = ({
    items = [],
    vendorId,
    vendor,
    tdsSlabs,
    entityId,
    close,
    onApply,
    applying = false
}) => {

    const { tdsAtLineItemAutomationV2, tdsAtLineItemAutomation } = useSelector((state) => state.auth.config.plugins);

    const { tdsList, loader } = useTdsList({ vendorId, vendor, tdsSlabs, entityId });

    const [tds, setTds] = useState([]);
    const [selectedTds, setSelectedTds] = useState(undefined);
    const [selectedItems, setSelectedItems] = useState(() => items.map(item => item.id));
    const TDS_SLAB = useRef();

    const allTds = tdsList;
    const allSelected = items.length > 0 && selectedItems.length === items.length;
    const showSlabInput = selectedTds && !tdsAtLineItemAutomationV2 && !tdsAtLineItemAutomation;

    useEffect(() => {
        setTds(allTds)
    }, [allTds])

    // reset the editable slab only when a different section is picked, not on every keystroke
    useEffect(() => {
        if (TDS_SLAB && TDS_SLAB.current && selectedTds) {
            TDS_SLAB.current.value = selectedTds.tax || 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTds && selectedTds.id, showSlabInput])

    const toggleItem = (id) => {
        setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    }

    const onSave = () => {
        if (!selectedTds) {
            showToast({
                type: 'error',
                message: 'Please select a tds section'
            })
            return;
        }
        if (!selectedItems.length) {
            showToast({
                type: 'error',
                message: 'Please select atleast one line item'
            })
            return;
        }
        onApply({
            "tdsSlab": selectedTds.tax,
            "tdsCode": selectedTds.label,
            "tdsDesc": selectedTds.des,
            "id": selectedTds.id,
            ...(selectedTds && selectedTds.tdsMaster) ? { "tdsMaster": selectedTds.tdsMaster } : {},
            ...(selectedTds && selectedTds.type) ? { "type": selectedTds.type } : {}
        }, selectedItems)
    }

    return (
        <Modal
            show={true}
            title="Apply Tds To Line Items"
            des="Select a tds section once and apply it to the selected line items"
            buttonName={`Apply Tds${selectedItems.length ? ` (${selectedItems.length} Line Item${selectedItems.length > 1 ? 's' : ''})` : ''}`}
            stateLoading={applying}
            close={close}
            style={{
                save: {
                    padding: '12px 15px',
                    width: '100%',
                    textAlign: 'center'
                }
            }}
            onSave={onSave}
        >
            {loader ?
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                    <PageLoader />
                    <p style={{ fontWeight: 'bold' }}>TDS Sections are being fetched</p>
                </div>
                :
                <>
                    <SearchableSelect
                        reset={() => { setTds(allTds) }}
                        label="Select Tds"
                        placeholder="Select Tds"
                        onChange={val => {
                            if (val.length > 0) {
                                setTds(allTds.filter(item => {
                                    if (item.label.toLowerCase().includes(val.toLowerCase())) {
                                        return true;
                                    }
                                    if (item.des && item.des.toLowerCase().includes(val.toLowerCase())) {
                                        return true;
                                    }
                                    if (String(item.tax).toLowerCase().includes(val.toLowerCase())) {
                                        return true;
                                    }
                                    return false;
                                }));
                            } else {
                                setTds(allTds);
                            }
                        }}
                        select={item => {
                            setSelectedTds(item);
                        }}
                        value={selectedTds}
                        items={tds}
                        render={(item) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    readOnly
                                    checked={!!(selectedTds && (selectedTds.id === item.id))}
                                    style={{ cursor: 'pointer', flexShrink: 0 }}
                                />
                                <div>
                                    <p style={{ fontSize: '12px' }}>{item.label}</p>
                                    {item.des && <p className="des">{item.des}</p>}
                                    {item.otherRate != null && <p className="des">Other Rate: {item.otherRate}%</p>}
                                    {item.individualHufRate != null && <p className="des">Individual Huf Rate: {item.individualHufRate}%</p>}
                                    {item.tax && item.otherRate == null && item.individualHufRate == null && <p className="des">{item.tax}%</p>}
                                </div>
                            </div>
                        )}
                    />
                    {showSlabInput &&
                        <FunctionalInput
                            ref={TDS_SLAB}
                            title="Tds Slab"
                            type="number"
                            onChange={(e) => {
                                setSelectedTds({ ...selectedTds, tax: Number(e.target.value) })
                            }}
                        />
                    }
                    <div style={{ marginTop: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <p className='floating-top-label'>Line Items</p>
                            <p
                                style={{ color: 'var(--primary-color)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                onClick={() => setSelectedItems(allSelected ? [] : items.map(item => item.id))}>
                                {allSelected ? 'Unselect All' : 'Select All'}
                            </p>
                        </div>
                        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-color)', marginTop: 8 }}>
                            {items.map((item, index) => (
                                <div
                                    key={item.id}
                                    onClick={() => toggleItem(item.id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: 8,
                                        cursor: 'pointer',
                                        borderBottom: index === items.length - 1 ? 'none' : '1px solid var(--border-color)'
                                    }}>
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={selectedItems.includes(item.id)}
                                        style={{ cursor: 'pointer', flexShrink: 0 }}
                                    />
                                    <div>
                                        <p style={{ fontSize: 12 }}>{`${index + 1}). ${item.desc || '-'}`}</p>
                                        <p className="des">
                                            {item.tds && item.tds.tdsCode ? `Current Tds : ${item.tds.tdsCode} (${item.tds.tdsRate}%)` : 'No Tds Applied'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            }
        </Modal>
    )
}

export default BulkTdsLineItem;
